/**
 * Client-side helpers for the extended-family circles feature.
 *
 * `useMyCircles()` returns the list of circles the current member
 * belongs to, used by the compose flows' "Share with…" target picker.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from './api';
import { myCirclesQueryOptions } from './queries';
import { useAuth } from './auth';
import type { RosterEntry } from '../components/MentionTextarea';

export interface Circle {
  id: string;
  name: string;
  surfaces: { photos: boolean; prayer: boolean; chat: boolean };
  role: 'admin' | 'member';
  joinedAt: number;
  /** Epoch ms — push notifications from this circle are muted until
   *  this time. `null` means push is enabled. */
  mutePushAt: number | null;
}

/** A pending email invitation to bring a new family into a circle. */
export interface CircleInvitation {
  id: string;
  email: string;
  displayName: string | null;
  createdAt: number;
  expiresAt: number;
  acceptedAt: number | null;
}

/** Outstanding circle invitations (admin-only on the server). Disabled
 *  when `circleId` is null so non-admin callers can guard safely. */
export function useCircleInvitations(circleId: string | null | undefined) {
  return useQuery({
    queryKey: ['circle-invitations', circleId],
    queryFn: () =>
      api.get<{ invitations: CircleInvitation[] }>(`/circles/${circleId}/invitations`),
    enabled: !!circleId,
  });
}

export interface CircleMember {
  id: string;
  displayName: string;
  role: 'admin' | 'member';
  joinedAt: number;
  familyId: string;
  familyName: string;
}

export interface CircleDetail {
  circle: Circle;
  myRole: 'admin' | 'member';
  members: CircleMember[];
}

export function useMyCircles() {
  return useQuery(myCirclesQueryOptions());
}

/** Detail view for a single circle, including its full cross-household
 *  roster. Used by the Generations @-mention picker. Disabled when
 *  `circleId` is null/undefined so the same hook can guard family-tab
 *  callers safely. */
export function useCircleDetail(circleId: string | null | undefined) {
  return useQuery({
    queryKey: ['circle-detail', circleId],
    queryFn: () => api.get<CircleDetail>(`/circles/${circleId}`),
    enabled: !!circleId,
    staleTime: 60_000,
  });
}

/**
 * Returns a deduped merged roster for an @-mention picker: the user's
 * own household plus every member of the named circle. Self is left
 * in — MentionTextarea filters it out — and identity is by member id,
 * which is globally unique. Cross-household entries carry a familyName
 * label so the picker can disambiguate (two "Dad"s from different
 * households look identical without it).
 *
 * Dedup is by id and is robust against duplicates from either source
 * (auth.me.members or the circle roster) — important because dev D1s
 * can end up with same-name duplicate member rows from repeated test
 * flows.
 *
 * Returns undefined when circleId is null (caller passes that straight
 * through to MentionTextarea, which then defaults to the household-only
 * useAuth roster).
 */
export function useMentionRoster(
  circleId: string | null | undefined,
): RosterEntry[] | undefined {
  const auth = useAuth();
  const detail = useCircleDetail(circleId);
  return useMemo(() => {
    if (!circleId) return undefined;
    if (auth.state !== 'signed-in') return [];
    const ownFamilyId = auth.me.family.id;
    const seen = new Set<string>();
    const out: RosterEntry[] = [];
    // Household first — never labeled (the user knows their own family).
    for (const m of auth.me.members) {
      if (seen.has(m.id)) continue;
      seen.add(m.id);
      out.push({ id: m.id, displayName: m.displayName });
    }
    // Circle members second — labeled with their family name only when
    // they're cross-household. Same-household entries that happen to
    // also be circle members were already added above.
    if (detail.data) {
      for (const m of detail.data.members) {
        if (seen.has(m.id)) continue;
        seen.add(m.id);
        out.push({
          id: m.id,
          displayName: m.displayName,
          familyName: m.familyId === ownFamilyId ? undefined : m.familyName,
        });
      }
    }
    return out;
  }, [circleId, auth, detail.data]);
}
